import {GroupButtonForm} from 'src/components/group-button-form';
import {Form} from '@unform/web';
import {useRef} from 'react';
import {FormGroup, Input, Label} from 'reactstrap';
import {Input as UInput} from 'src/components/input';
import I18nMessage from 'src/components/I18nMessage';
import {FieldSet} from 'src/components/fieldset';
import {useCreateIntegration, useUpdateIntegration} from './mutations';
import {Integration, IntegrationCreateInput} from 'src/core/data/api/Integration';
import {ViewType} from './container';
import {createIntegrationSchema, updateIntegrationSchema} from './schemas';
import Yup from 'src/infra/Yup';
import useAlerts from 'src/hooks/use-alerts';
import {useIntl} from 'react-intl';

interface IntegrationCreateOrUpdateViewProps {
    onViewChange: (viewName: ViewType, integration?: Integration) => void;
    selectedIntegration?: Integration;
}

const IntegrationCreateOrUpdateView = ({onViewChange, selectedIntegration}: IntegrationCreateOrUpdateViewProps) => {
    const formRef = useRef(null);
    const {toast} = useAlerts();
    const intl = useIntl();

    const {mutate: createIntegration, isPending: isCreatePending} = useCreateIntegration();
    const {mutate: updateIntegration, isPending: isUpdatePending} = useUpdateIntegration();

    const isEditing = !!selectedIntegration;

    const onSubmitError = (err: any) => {
        const validationErrors: Record<string, string> = {};
        if (err instanceof Yup.ValidationError) {
            err.inner.forEach((error) => {
                if (error.path) {
                    validationErrors[error.path] = error.message;
                }
            });
            // @ts-ignore
            formRef.current?.setErrors(validationErrors);
            console.log('Validation Errors:', validationErrors);
        } else {
            console.log('Unexpected Error:', err);
        }
    };

    const onApiError = (error: any) => {
        toast.errorStatus(error);
    };

    const onCreateSuccess = () => {
        toast.success(intl.formatMessage({id: 'sharedSavedSuccess'}));
        onViewChange('list');
    };

    const onUpdateSuccess = () => {
        toast.success(intl.formatMessage({id: 'sharedUpdatedSuccess'}));
        onViewChange('list');
    };

    const onCancel = () => {
        onViewChange('list');
    };

    const onSubmit = (payload: any) => {
      // @ts-ignore
        formRef.current?.submitForm(payload);
    };

    const handleCreate = async (data: IntegrationCreateInput) => {
        const result = await createIntegrationSchema.validate(data, {abortEarly: false});
        createIntegration(result, {
            onSuccess: onCreateSuccess,
            onError: onApiError,
        });
    };

    const handleUpdate = async (data: Integration) => {
        const result = await updateIntegrationSchema.validate(data, {abortEarly: false});
        updateIntegration(result, {
            onSuccess: onUpdateSuccess,
            onError: onApiError,
        });
    };

    const handleSubmit = async (data: Integration | IntegrationCreateInput) => {
        if (isEditing) {
            try {
                await handleUpdate(data as Integration);
            } catch (err) {
                onSubmitError(err);
            }
        } else {
            try {
                await handleCreate(data);
            } catch (err) {
                onSubmitError(err);
            }
        }
    };

    return (
        <Form ref={formRef} onSubmit={handleSubmit} initialData={selectedIntegration ?? {
            title: '',
            description: '',
            url: '',
        }}>
            {isEditing && <UInput hidden type="number" name="id" />}
            <FieldSet className="text-left border p-4" title={intl.formatMessage({id: 'sharedIdentification'})}>
                <div className='row'>
                    <div className='col'>
                        <FormGroup className="custom-formGroup">
                            <Label for="title"><I18nMessage id="sharedName" />*</Label>
                            <Input
                                tag={UInput}
                                type="text"
                                name="title"
                                id="title"
                            />
                        </FormGroup>
                        <FormGroup className="custom-formGroup">
                            <Label for="description"><I18nMessage id="sharedDescription" />*</Label>
                            <Input
                                tag={UInput}
                                type="text"
                                name="description"
                                id="description"
                            />
                        </FormGroup>
                        <FormGroup className="custom-formGroup">
                            <Label for="url"><I18nMessage id="URL" />*</Label>
                            <Input
                                tag={UInput}
                                type="text"
                                name="url"
                                id="url"
                            />
                        </FormGroup>
                    </div>
                </div>
            </FieldSet>
            <br />

            <GroupButtonForm
                loading={isCreatePending || isUpdatePending}
                cancel={onCancel}
                // @ts-ignore
                submit={onSubmit}
            />
        </Form>
    );
};

export default IntegrationCreateOrUpdateView;
